import { ArrowRight, ArrowLeft } from 'lucide-react'
import { useLatestSession } from 'src/hooks/useSession'
import SimpleLayout from 'src/layouts/SimpleLayout'
import Docs from 'src/components/Docs'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { LanguageCode } from 'src/models/language'
import useAuth from 'src/hooks/useAuth'
import useDocs, { DocumentMetadata } from 'src/hooks/useDocs'
import Spinner from 'src/components/Spinner'
import LanguageSelectBar from 'src/components/LanguageSelectBar'
import useOrgs from 'src/hooks/useOrgs'
import OrgBanner from 'src/components/OrgBanner'
import { Org } from 'src/hooks/useOrg'
import { Link, useNavigate } from 'react-router-dom'
import Button from 'src/components/Button'
import { cn } from 'src/utils'
import useSessions from 'src/hooks/useSessions'
import { Tabs } from '@radix-ui/react-tabs'
import { TabsList, TabsTrigger } from 'src/components/ui/Tabs'

type DifficultyLevel = '0' | '1' | '2';

const ExplorePage = () => {
  const {
    latestSession,
    isLoading: isLatestSessionLoading,
  } = useLatestSession()
  const navigate = useNavigate()
  const { isLoggedIn } = useAuth()
  const { orgs, isLoading: isOrgsLoading } = useOrgs()

  const [fromLang, setFromLang] = useState<LanguageCode>(
    LanguageCode.SpanishSpain,
  )
  const [toLang, setToLang] = useState<LanguageCode>(
    LanguageCode.EnglishUs,
  )
  const [difficulty, setDifficulty] = useState<DifficultyLevel | undefined>();

  useEffect(() => {
    if (!latestSession) return
    setToLang(latestSession?.toLang)
    setFromLang(latestSession?.fromLang)
  }, [latestSession])

  const {
    sessions,
    isLoading: isSessionsLoading,
  } = useSessions({
    fromLang,
    toLang,
  });

  const {
    docs,
    next,
    prev,
    isNext,
    isPrev,
    isLoading,
    isNextLoading,
  } = useDocs({
    fromLang,
    toLang,
    difficulty,
    ...(!isLoggedIn ? { groupId: 'demo' } : {}),
  })

  const hasLatestSession = isLatestSessionLoading
    ? true
    : !!latestSession

  const unseenDocs: DocumentMetadata[] = useMemo(() => {
    const sessionDocIds = new Set((sessions ?? []).map(({ documentId }) => documentId));
    return (docs ?? []).filter(({ id }) => {
      if (sessionDocIds.has(id)) return false;
      sessionDocIds.add(id);
      return true;
    });
  }, [docs, sessions]);

  const onNavigate = useCallback(
    (p: string) => 
      isLoggedIn && !hasLatestSession
        ? navigate(
            `/tutorials/reader?returnTo=${encodeURIComponent(
              p,
            )}`,
          )
        : navigate(p),
    [isLoggedIn, hasLatestSession, navigate],
  )

  return (
    <SimpleLayout className="justify-start">
      {(isLoading || isOrgsLoading) && (
        <div className="min-h-4 mx-auto mb-4 block">
          <Spinner />
        </div>
      )}

      {!isLoading && orgs && (
        <div className="mb-8 flex w-full max-w-sm flex-col gap-2">
          {orgs.map((org: Org) => (
            <Link
              key={org.id}
              to={`/orgs/${org.id}`}
              className="transition-all ease-in-out hover:opacity-90"
            >
              <OrgBanner variant="mini" {...org} />
            </Link>
          ))}
        </div>
      )}

      {!isLoading && (
        <div className="mb-12 flex w-full max-w-sm justify-center">
          <LanguageSelectBar
            fromLang={fromLang}
            toLang={toLang}
            setFromLang={setFromLang}
            setToLang={setToLang}
          />
        </div>
      )}

    {!isSessionsLoading && (
        <section className="mb-4 w-full max-w-sm justify-center">
          {!!sessions?.length && <h2 className="text-center mb-2">Continue reading</h2>}
          {sessions && (
            <Docs
              session={latestSession}
              docs={sessions}
              orgs={orgs}
              fromLang={fromLang}
              toLang={toLang}
              onNavigate={onNavigate}
            />
          )}
        </section>
      )}

      {!isLoading && (
        <section className="mb-4 w-full max-w-sm justify-center">
          {docs && (
            <>
              <h2 className="text-center mb-2">Explore</h2>
              <div className="mb-2 flex justify-center">
                <Tabs value={difficulty} onValueChange={(d) => setDifficulty(d as DifficultyLevel)}>
                  <TabsList>
                    <TabsTrigger value="0">Beginner</TabsTrigger>
                    {/* Uncategorized books */}
                    <TabsTrigger value={undefined as unknown as string}>Intermediate/Advanced</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <Docs
                session={latestSession}
                docs={unseenDocs}
                orgs={orgs}
                fromLang={fromLang}
                toLang={toLang}
                onNavigate={onNavigate}
              />
            </>
          )}

          <div
            className={cn(
              'mx-auto mb-4 w-fit transition-all ease-in-out',
              !isNextLoading && 'h-0 opacity-0',
              isNextLoading && 'h-4 opacity-100',
            )}
          >
            <Spinner />
          </div>

          <div className="mt-8 flex justify-evenly">
            <Button
              disabled={!isPrev}
              onClick={() => prev()}
            >
              <ArrowLeft />
            </Button>

            <Button
              disabled={isNextLoading || !isNext}
              onClick={() => next()}
            >
              <ArrowRight />
            </Button>
          </div>
        </section>
      )}
    </SimpleLayout>
  )
}

export default ExplorePage
